import PaginationComponent from '../Footers/Pagination.tsx';
import { shopDueDate } from '../ShopShipDate';
import {
	Empty,
	Tag,
	TableProps,
	Tabs,
	Select,
	message,
	Spin,
	Skeleton,
	Modal,
	ConfigProvider,
	Typography,
} from 'antd';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useGetOrderLineItemsQuery } from '../../redux/api/order-line-item.ts';
import {
	getOrderLineItemProductionStatusTagColor,
	getProductionMethodTagColor,
} from '../../utils';
import CustomResponsiveTable from '../CustomResponsiveTable/index.tsx';
import { useEffect, useState } from 'react';
import { productionQueueTabs } from '../../config/constants.ts';
import { useUpdateOrderLineItemMutation } from '../../redux/api/order.ts';
import { useLazyGetGroupedRelatedMaterialReservationsQuery } from '../../redux/api/order-line-item.ts';
import { THEME } from '../../main.tsx';
import { useCreateBallooningProjectMutation } from '../../redux/api/ballooning.ts';
import MaterialRequestTable from '../MaterialRequestTable';
import { statuses } from '../../config/constants.ts';
import { SalesOrderFilter } from '../../pages/Orders/SalesOrderFilter.tsx';
import { useBallooningStatusChange } from '../../hooks/useBallooningStatusChange';
import { getInspectionProjectId } from '../../utils/inspectionProjectId';

const ProductionQueueTables = () => {
	const navigate = useNavigate();

	const [searchParams, setSearchParams] = useSearchParams();
	const [lineItemQuery, setLineItemQuery] = useState({});
	const [clickedRowKey, setClickedRowKey] = useState<string | null>(null);
	const [displayTag, setDisplayTag] = useState(true);
	const [updateOrderLineItem] = useUpdateOrderLineItemMutation();
	const [createBallooningProject] = useCreateBallooningProjectMutation();
	const [isCreatingProject, setIsCreatingProject] = useState(false);
	const [triggerGetGroupedReservations] = useLazyGetGroupedRelatedMaterialReservationsQuery();
	const { handleBallooningStatusChange } = useBallooningStatusChange();

	// Initialize query from URL parameters
	useEffect(() => {
		const productionMethod = searchParams.get('production_method');
		const building = searchParams.get('building');
		const queue = searchParams.get('queue');
		const page = searchParams.get('page');
		const ordering = searchParams.get('ordering');

		const newQuery: Record<string, any> = {
			is_queues: true,
			created_time_min: '2025-03-01',
		};

		if (queue) newQuery.queue = queue;
		if (page) newQuery.page = page;
		if (ordering) newQuery.ordering = ordering;
		if (productionMethod) newQuery.partsbadger_production_method = productionMethod;
		if (building) newQuery.building = building;

		setLineItemQuery(newQuery);
	}, [searchParams]);

	const { data, isLoading, isFetching, refetch } = useGetOrderLineItemsQuery(
		lineItemQuery,
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const refetchData = () => {
		refetch();
	};

	const handleTableChange: TableProps<Lineitem>['onChange'] = (
		_pagination,
		_filters,
		sorter,
		extra
	) => {
		if (extra.action === 'sort') {
			const orderType = (sorter as SorterResult<Lineitem>)?.order;
			const columnKey = (sorter as SorterResult<Lineitem>)?.columnKey;
			const sorterColumn = (sorter as SorterResult<Lineitem>)?.column;

			const column =
				orderType === 'ascend' || orderType === undefined
					? columnKey
					: '-' + columnKey;
			if (column !== undefined && sorterColumn !== undefined) {
				searchParams.set('ordering', column?.toString());
			} else {
				searchParams.set('ordering', 'scheduling_start_date');
			}
			navigate({ search: searchParams.toString() });
		}
	};

	const handleRowClick = (record: Lineitem) => {
		const activeTab = searchParams.get('queue');
		if (activeTab === 'ballooning_status') {
			const pid = getInspectionProjectId(record);
			if (pid) {
				navigate(`/ballooning/${pid}`);
			} else {
				message.warning('No ballooning project ID available for this item.');
			}
		}
	};

	const tableColumns: ColumnsType<any> = [
		{
			title: 'Preview',
			render: (record: Lineitem) => (
				<img className="h-16" src={record.thumbnail_file} />
			),
			key: 'thumbnail',
		},
		{
			title: 'Name',
			key: 'name',
			render: (record: Lineitem) => (
				<div>
					<Link
						to={`/orders/line-items/${record?.id}`}
						onClick={(e) => e.stopPropagation()}
					>
						{record.name}
					</Link>
					{record.is_internal && (
						<div className="mt-1">
							<Tag color="purple">Internal</Tag>
						</div>
					)}
				</div>
			),
			sorter: true,
		},
		{
			title: 'Order',
			key: 'order__name',
			render: (record: Lineitem) =>
				record.order?.id ? (
					<Link
						to={`/orders/${record.order.id}`}
						onClick={(e) => e.stopPropagation()}
					>
						{record.order.name}
					</Link>
				) : (
					<Typography.Text type="secondary">—</Typography.Text>
				),
			sorter: true,
		},
		{
			title: 'Quantity',
			key: 'quantity',
			render: (record: Lineitem) => `${record.quantity}`,
			sorter: true,
		},
		{
			title: 'Method',
			key: 'partsbadger_production_method',
			// Method shows only the actual production method. The Internal
			// marker lives under the part name (as on the orders/OLI tables) —
			// putting it here read as "method: Internal" for internal items,
			// which have no method assigned yet.
			render: (record: Lineitem) =>
				record.partsbadger_production_method ? (
					<Tag
						color={getProductionMethodTagColor(
							record.partsbadger_production_method
						)}
					>
						{record.partsbadger_production_method}
					</Tag>
				) : (
					<Typography.Text type="secondary">—</Typography.Text>
				),
			sorter: true,
		},
		{
			title: 'Received Date',
			key: 'created_time',
			render: (record: Lineitem) =>
				record?.created_time
					? dayjs(record.created_time).format('YYYY-MM-DD')
					: '',
			sorter: true,
		},
		{
			title: 'Required Ship Date',
			key: 'required_partner_ship_date',
			render: (record: Lineitem) =>
				shopDueDate(record)
					? dayjs(shopDueDate(record)).format('YYYY-MM-DD')
					: '',
			sorter: true,
		},
		{
			title: 'Job',
			key: 'scheduling_job_id',
			width: 60,
			render: (record: Lineitem) =>
				record.scheduling_job_id ? (
					<Link
						to={`/scheduling/jobs/${record.scheduling_job_id}`}
						target="_blank"
						onClick={(e) => e.stopPropagation()}
					>
						<Tag color="blue">Job</Tag>
					</Link>
				) : null,
		},
	];

	const tableFooter = () => {
		return (
			<div className="flex justify-between items-center">
				<div className="ml-auto">
					<PaginationComponent
						pageSize={20}
						total={data?.count}
						showSizeChanger={false}
					/>
				</div>
			</div>
		);
	};

	const emptyTable = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						There are currently no line items to be displayed.
					</span>
				}
			/>
		);
	};

	const handleTabChange = (key: string) => {
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.set('queue', key);
		newSearchParams.set('page', '1');
		setSearchParams(newSearchParams);
		setLineItemQuery({ ...lineItemQuery, queue: key });
	};

	useEffect(() => {
		if (
			!searchParams.has('queue') ||
			!searchParams.get('page') ||
			!searchParams.has('ordering')
		) {
			setSearchParams({
				...searchParams,
				queue: 'ballooning_status',
				page: '1',
				ordering: 'scheduling_start_date',
			});
			setLineItemQuery({
				...lineItemQuery,
				queue: 'ballooning_status',
				page: 1,
				ordering: 'scheduling_start_date',
			});
		}
	}, []);

	const handleShowSelectStatus = (id: string) => {
		if (clickedRowKey === id) {
			setDisplayTag(!displayTag);
		} else {
			setClickedRowKey(id);
			setDisplayTag(false);
		}
	};

	const handleStatusChange = (
		id: string,
		value: string,
		field_name: string
	) => {
		const payload = {
			id: id,
			values: { [field_name]: value },
		};

		updateOrderLineItem(payload)
			.unwrap()
			.then(() => {
				const text = `Successfully updated ${field_name
					.toUpperCase()
					.replace(/_/g, ' ')} to ${value}`;
				message.success(text);
				refetchData();
			})
			.catch(() => message.error(`Failed to update ${field_name} status`));

		setDisplayTag(true);
		setClickedRowKey(null);
	};

	const handleBallooningStatusChangeLocal = async (
		id: string,
		value: string,
		_field_name: string,
		record: Lineitem
	) => {
		await handleBallooningStatusChange({
			lineItem: record,
			newStatus: value,
			prevStatus: record.ballooning_status,
			options: { navigateOnInProgress: true, navigateOnCompleted: false },
		});
		refetchData();
		setDisplayTag(true);
		setClickedRowKey(null);
	};

	const handleStatusChangeWrapper = (
		id: string,
		value: string,
		field_name: string,
		record: Lineitem
	) => {
		const activeTab = searchParams.get('queue');

		if (activeTab === 'ballooning_status') {
			handleBallooningStatusChangeLocal(id, value, field_name, record);
		} else if (activeTab === 'material_prep_status') {
			// Front-end validation: CAM must be Completed to allow Material Prep changes
			if (
				(value === 'Completed' || value === 'In Progress') &&
				record.cam_programming_status !== 'Completed'
			) {
				message.warning('CAM Programming needs to be completed prior to updating Material Prep.');
				setDisplayTag(true);
				setClickedRowKey(null);
				return;
			}

			// Match prior queue behavior: In Progress routes to Cutting instead of patching
			if (value === 'In Progress') {
				navigate(`/tablet/operations/cutting?group_cut_status=Pending&search=${record.name}`);
				setDisplayTag(true);
				setClickedRowKey(null);
				return;
			}

			// If attempting to mark as Completed, verify that some material has been cut
			if (value === 'Completed') {
				(async () => {
					try {
						const res = await triggerGetGroupedReservations({ id }).unwrap();
						const groups = (res as any[]) || [];
						const anyCut = groups.some((g: any) =>
							Array.isArray(g?.reservations) && g.reservations.some((r: any) => (r?.material_cut_status === 'Cut') || ((r?.actual_cut_quantity || 0) > 0))
						);
						if (!anyCut) {
							Modal.confirm({
								centered: true,
								title: 'Material Prep Requires Cutting',
								icon: null,
								content: 'No material appears to be cut for this job. Please cut material in the Material (Cutting) section before marking Material Prep as Completed.',
								onOk: () => navigate(`/tablet/operations/cutting?group_cut_status=Pending&search=${record.name}`),
								onCancel: () => { },
								footer: (_, { OkBtn, CancelBtn }) => (
									<ConfigProvider theme={THEME}>
										<CancelBtn />
										<OkBtn />
									</ConfigProvider>
								),
							});
							setDisplayTag(true);
							setClickedRowKey(null);
							return;
						}
						// proceed to patch
						handleStatusChange(id, value, field_name);
					} catch (e) {
						// On error, fall back to navigation prompt
						Modal.confirm({
							centered: true,
							title: 'Material Prep Requires Cutting',
							icon: null,
							content: 'Unable to verify cutting status. Please proceed to the Material (Cutting) section to complete cutting before marking as Completed.',
							onOk: () => navigate(`/tablet/operations/cutting?group_cut_status=Pending&search=${record.name}`),
							onCancel: () => { },
							footer: (_, { OkBtn, CancelBtn }) => (
								<ConfigProvider theme={THEME}>
									<CancelBtn />
									<OkBtn />
								</ConfigProvider>
							),
						});
						setDisplayTag(true);
						setClickedRowKey(null);
					}
				})();
				return;
			}

			handleStatusChange(id, value, field_name);
		} else {
			handleStatusChange(id, value, field_name);
		}
	};

	// Logic for the dynamic options of the Select
	const activeQueueFieldName = searchParams.get('queue');
	const currentQueueStatuses = statuses.find(
		(s) => s.field_name === activeQueueFieldName
	);
	const optionsForSelect = currentQueueStatuses
		? currentQueueStatuses.statuses
		: [];

	return isLoading ? (
		<Skeleton />
	) : (
		<div>
			<div className="flex justify-end" style={{ marginTop: '-50px' }}>
				<SalesOrderFilter showSearchInput={false} showBuilding={true} />
			</div>
			<Spin spinning={isCreatingProject} tip="Creating project, please wait...">
				<Tabs
					activeKey={searchParams.get('queue') || 'ballooning_status'}
					size="small"
					type="card"
					tabBarStyle={{ marginBottom: 0 }}
					onChange={handleTabChange}
					items={productionQueueTabs.map(({ label, field_name }) => {
						return {
							label: label,
							key: field_name,
							children: (
								<div className="bg-white border-r border-l border-b w-full pt-4">
									{searchParams.get('queue') === 'material_ordering_status' ? (
										<div className={'p-4'}>
											<MaterialRequestTable hiddenMaterialRequests={true} />
										</div>
									) : (
									<CustomResponsiveTable
										columns={[
											...tableColumns,
										...(['ballooning_status', 'keyence_programming_status', 'programming_template_status', 'tooling_selection_status', 'material_selection_status', 'pb_prod_accepted_status', 'cam_programming_status', 'tooling_ordering_status', 'material_prep_status', 'cart_assembly_status', 'machine_setup_status', 'machining_status'].includes(field_name) ? [{
											title: 'Building',
											key: 'building__name',
											render: (record: Lineitem) => record.building?.name ?? '—',
											sorter: true,
										}] : []),
											{
												title: `${label} Status`,
													key: field_name,
													fixed: 'right',
													render: (record: Lineitem) => (
														<div
															onClick={(e) => {
																e.stopPropagation();
																handleShowSelectStatus(record.id);
															}}
															style={{ cursor: 'pointer' }}
														>
															{clickedRowKey === record.id && !displayTag ? (
																<div>
																	<p style={{ whiteSpace: 'normal' }}>
																		Select production status:
																	</p>
																	<Select
																		value={record[field_name]}
																		onSelect={(value) =>
																			handleStatusChangeWrapper(
																				record.id,
																				value,
																				field_name,
																				record
																			)
																		}
																		style={{ width: 200 }}
																		onClick={(e) => e.stopPropagation()}
																	>
																		{optionsForSelect.map((optionValue) => (
																			<Select.Option
																				key={optionValue}
																				value={optionValue}
																			>
																				{optionValue}
																			</Select.Option>
																		))}
																	</Select>
																</div>
															) : (
																<Tag
																	style={{ whiteSpace: 'normal' }}
																	color={getOrderLineItemProductionStatusTagColor(
																		record[field_name]
																	)}
																>
																	{record[field_name]}
																</Tag>
															)}
														</div>
													),
													sorter: (a: Lineitem, b: Lineitem) => {
														if (a[field_name] < b[field_name]) return -1;
														if (a[field_name] > b[field_name]) return 1;
														return 0;
													},
												},
											]}
											data={data?.results}
											pagination={false}
											loading={isLoading || isFetching}
											footer={tableFooter}
											scroll={{ y: `calc(95vh - 250px)` }}
											onChange={handleTableChange}
											emptyTable={emptyTable}
											onRow={(record) => ({
												onClick: () => handleRowClick(record),
											})}
										/>
									)}
								</div>
							),
						};
					})}
				/>
			</Spin>
		</div>
	);
};

export default ProductionQueueTables;
